import { useEffect, useMemo, useState } from "react";

import { Bloco, Cartao, Vazio } from "./componentes/Cartao";
import { CaixaDeDecisao } from "./componentes/CaixaDeDecisao";
import { Grafico } from "./componentes/Grafico";
import { LoginPage } from "./componentes/LoginPage";
import { TabelaCriticos, TabelaExcesso, TabelaFamilias } from "./componentes/Tabela";
import {
  ErroDeApi, carregarCatalogo, carregarPainel, definirAoDeslogar, guardarToken, obterToken,
  type Catalogo, type Painel,
} from "./api";
import {
  capitalParado, dataBr, indicadores, opcaoFilial, opcaoMensal,
  tabelaCriticos, tabelaExcesso, tabelaFamilias,
} from "./leitura.js";

/**
 * Painel executivo (roadmap 6, ADR-05).
 *
 * A tela inteira faz **uma** requisição: `/api/analytics/paineis/executivo`. Não
 * são sete chamadas montadas no navegador porque o painel só existe inteiro — os
 * cartões e o gráfico precisam falar do mesmo período, e sete respostas chegando
 * fora de ordem produzem o pior tipo de erro, o que parece certo.
 *
 * O seletor de período também não é uma lista fixa aqui: vem de
 * `/api/analytics/catalogo`. Assim, um período novo aparece no seletor no
 * mesmo deploy em que passa a ser aceito, e nunca antes.
 */
export default function App() {
  const [autenticado, setAutenticado] = useState(() => Boolean(obterToken()));
  const [view, setView] = useState<"painel" | "decisao">("painel");
  const [catalogo, setCatalogo] = useState<Catalogo | null>(null);
  const [painel, setPainel] = useState<Painel | null>(null);
  const [nome, setNome] = useState("executivo");
  const [periodo, setPeriodo] = useState("ultimos_12_meses");
  const [filial, setFilial] = useState<string>("");
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<{ texto: string; status: number } | null>(null);

  // Uma sessão expirada (401 em qualquer chamada) volta pro login sozinha —
  // sem isto, cada tela teria que checar o mesmo status separadamente.
  useEffect(() => {
    definirAoDeslogar(() => setAutenticado(false));
    return () => definirAoDeslogar(null);
  }, []);

  useEffect(() => {
    if (!autenticado || view !== "painel") return;
    carregarCatalogo().then(setCatalogo).catch(() => setCatalogo(null));
  }, [autenticado, view]);

  useEffect(() => {
    if (!autenticado || view !== "painel") return;
    let vivo = true;
    setCarregando(true);
    setErro(null);
    carregarPainel(nome, { periodo, filial: filial || null })
      .then((p) => { if (vivo) { setPainel(p); setErro(null); } })
      .catch((e: unknown) => {
        if (!vivo) return;
        const status = e instanceof ErroDeApi ? e.status : 0;
        setErro({ texto: e instanceof Error ? e.message : "falha ao carregar", status });
      })
      .finally(() => { if (vivo) setCarregando(false); });
    // A resposta de um período que o usuário já trocou não pode sobrescrever a
    // do período atual. Sem esta trava, trocar rápido no seletor deixa a tela
    // mostrando o período errado — e nada na tela indica isso.
    return () => { vivo = false; };
  }, [autenticado, view, nome, periodo, filial]);

  const blocos = painel?.blocos ?? {};
  const kpis = useMemo(() => indicadores(blocos), [blocos]);
  const parado = useMemo(() => capitalParado(blocos), [blocos]);
  const optMensal = useMemo(() => opcaoMensal(blocos.comercial_mensal), [blocos]);
  const optFilial = useMemo(() => opcaoFilial(blocos.comercial_por_filial), [blocos]);
  const criticos = useMemo(() => tabelaCriticos(blocos.itens_criticos), [blocos]);
  const excedentes = useMemo(() => tabelaExcesso(blocos.excesso), [blocos]);
  const familias = useMemo(() => tabelaFamilias(blocos.comercial_por_familia), [blocos]);

  const filiaisDisponiveis = (blocos.comercial_por_filial ?? []).map((l) => ({
    codigo: String(l["desempenho_comercial.filial_codigo"]),
    nome: String(l["desempenho_comercial.filial_nome"]),
  }));

  if (!autenticado) {
    return <LoginPage onEntrar={() => setAutenticado(true)} />;
  }

  function sair() {
    guardarToken(null);
    setAutenticado(false);
  }

  return (
    <div className="app">
      <nav className="abas" aria-label="Seções do Nexus">
        <button type="button" className={view === "painel" ? "ativa" : ""} onClick={() => setView("painel")}>
          Painel
        </button>
        <button type="button" className={view === "decisao" ? "ativa" : ""} onClick={() => setView("decisao")}>
          Caixa de decisão
        </button>
        <button type="button" className="sair" onClick={sair}>Sair</button>
      </nav>

      {view === "decisao" && (
        <>
          <header className="topo"><h1>Caixa de decisão</h1></header>
          <CaixaDeDecisao />
        </>
      )}

      {view === "painel" && (
      <>
      <header className="topo">
        <div>
          <h1>{painel?.titulo ?? "Painel executivo"}</h1>
          {painel && (
            <p className="periodo">
              {painel.periodo.rotulo} · {dataBr(painel.periodo.de)} a {dataBr(painel.periodo.ate)}
              {painel.filial && ` · filial ${painel.filial}`}
              {painel._meta.modo === "demo" && <span className="selo tom-atencao">dados de demonstração</span>}
            </p>
          )}
        </div>

        <div className="controles">
          <label>
            <span className="sr">Painel</span>
            <select value={nome} onChange={(e) => setNome(e.target.value)}>
              {(catalogo?.paineis ?? [{ nome: "executivo", titulo: "Painel executivo" }]).map((p) => (
                <option key={p.nome} value={p.nome}>{p.titulo}</option>
              ))}
            </select>
          </label>
          <label>
            <span className="sr">Período</span>
            <select value={periodo} onChange={(e) => setPeriodo(e.target.value)}>
              {(catalogo?.periodos ?? [{ nome: "ultimos_12_meses", rotulo: "Últimos 12 meses" }]).map((p) => (
                <option key={p.nome} value={p.nome}>{p.rotulo}</option>
              ))}
            </select>
          </label>
          <label>
            <span className="sr">Filial</span>
            <select value={filial} onChange={(e) => setFilial(e.target.value)}>
              <option value="">Todas as filiais</option>
              {filiaisDisponiveis.map((f) => (
                <option key={f.codigo} value={f.codigo}>{f.nome}</option>
              ))}
            </select>
          </label>
        </div>
      </header>

      {erro && (
        <div className={`aviso ${erro.status === 403 ? "negado" : "falha"}`} role="alert">
          <strong>{tituloDoErro(erro.status)}</strong>
          <span>{erro.texto}</span>
          {/* Um 403 de filial é recusa, não defeito: o BFF prefere negar a
              devolver um gráfico vazio que o usuário leria como "não vendeu". */}
          {erro.status === 403 && (
            <button type="button" onClick={() => setFilial("")}>Ver todas as filiais</button>
          )}
        </div>
      )}

      <div className={carregando ? "conteudo carregando" : "conteudo"} aria-busy={carregando}>
        <div className="cartoes">
          {kpis.map((i) => <Cartao key={i.chave} i={i} />)}
          <article className="cartao tom-neutro">
            <h3>Capital parado</h3>
            <p className="valor">{parado.valor}</p>
            <p className="nota">
              {parado.itens} itens sem consumo
              {parado.participacao && ` · ${parado.participacao} do estoque`}
            </p>
          </article>
        </div>

        {blocos.comercial_mensal && (
          <Bloco titulo="Faturamento e margem por mês" subtitulo="Barra em reais, linha em percentual" largo>
            <Grafico opcao={optMensal} altura={300} />
          </Bloco>
        )}

        {blocos.comercial_por_filial && (
          <Bloco titulo="Por filial" subtitulo="Faturamento no período">
            <Grafico opcao={optFilial} altura={Math.max(180, filiaisDisponiveis.length * 38)} />
          </Bloco>
        )}

        {blocos.comercial_por_familia && (
          <Bloco titulo="Por família" subtitulo="Onde está o volume e onde está a margem">
            <TabelaFamilias linhas={familias} />
          </Bloco>
        )}

        {blocos.itens_criticos && (
          <Bloco titulo="O que falta hoje" subtitulo="Ordenado pelo consumo diário, não pelo saldo" largo>
            {criticos.length
              ? <TabelaCriticos linhas={criticos} />
              : <Vazio>Nenhum item em ruptura ou com cobertura crítica no recorte atual.</Vazio>}
          </Bloco>
        )}

        {blocos.excesso && (
          <Bloco titulo="Capital parado" subtitulo="Itens sem consumo ou com cobertura excessiva" largo>
            {excedentes.length
              ? <TabelaExcesso linhas={excedentes} />
              : <Vazio>Nenhum item com excesso relevante no recorte atual.</Vazio>}
          </Bloco>
        )}
      </div>

      <footer className="rodape">
        <p>
          Todos os números vêm da camada semântica governada (ADR-05). O painel formata e ordena;
          nenhuma métrica é calculada no navegador.
          {painel && ` Consulta em ${painel._meta.ms} ms.`}
        </p>
      </footer>
      </>
      )}
    </div>
  );
}

function tituloDoErro(status: number) {
  if (status === 401) return "Sessão expirada.";
  if (status === 403) return "Sem permissão para esta filial.";
  if (status === 400) return "Pedido inválido.";
  if (status === 502) return "A camada semântica não respondeu.";
  return "Não foi possível carregar o painel.";
}
